import React, { useState, useRef } from 'react';
import { 
  Box, 
  Card, 
  CardContent,
  Button,
  ButtonGroup,
} from '@mui/material';
import { ApiProductivityResponse } from '../../types/api';
import { ExportService } from '../../services/exportService';
import { GenericTable } from '../../components/GenericTable';
import { GenericBarChart } from '../../components/GenericBarChart';
import { GenericCurveChart } from '../../components/GenericCurveChart';
import {
  transformProductivityTableData,
  productivityTableColumns,
  transformProductivityChartData,
  transformProductivityCurveData,
  getProductivityRowBackgroundColor,
  getProductivityRowTextColor,
  getProductivityCellBackgroundColor,
  getProductivityCellTextColor,
  PRODUCTIVITY_LABELS
} from '../../transforms/productivityTransforms';
import { FilterControls } from '../../components/FilterControls';
import { useSearchParams } from 'react-router-dom';

interface ProductivityChartViewProps {
  data: ApiProductivityResponse;
  period: 'week' | 'day' | 'all' | 'year';
  onPeriodChange: (period: 'week' | 'day' | 'all' | 'year') => void;
  dateRange: { start: string; end: string };
  setDateRange: (range: { start: string; end: string }) => void;
  startDate: string;
  endDate: string;
}

const exportService = new ExportService();

export const ProductivityChartView: React.FC<ProductivityChartViewProps> = ({ 
  data, 
  period, 
  onPeriodChange,
  dateRange,
  setDateRange,
  startDate,
  endDate,
}) => {
  const [searchParams] = useSearchParams();
  const hideDownloadOptions = searchParams.get('hideDownloadOptions') || 'false';
  const [currentView, setCurrentView] = useState<'chart' | 'table' | 'curve'>('chart');
  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const curveRef = useRef<HTMLDivElement>(null);

  const tableData = transformProductivityTableData(data, period as any);
  const chartData = transformProductivityChartData(data, period as any, {
    showBudgetedProductivity: true,
    showOtherData: false
  });
  const curveData = transformProductivityCurveData(data, period as any);

  const handleExportExcel = async () => {
    try {
      await exportService.exportToExcelV2(
        [
          period === 'week' ? 'Semana' : 'Día',
          PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY,
          PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY,
          PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY,
        ],
        tableData.map(row => [
          row[PRODUCTIVITY_LABELS.PERIOD],
          row[PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY],
          row[PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY],
          row[PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY]
        ]),
        `${data.Name}_productividad`
      );
    } catch (error) {
      alert('Error al exportar a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      let elementRef = chartRef;
      if (currentView === 'curve') elementRef = curveRef;
      if (!elementRef.current) {
        return
      }

      await exportService.exportProductivityToPDF(data, elementRef.current);
    } catch (error) {
      alert('Error al exportar a PDF');
    }
  };

  return (
    <Box sx={{ width: '100%', margin: '0 auto' }}>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '1rem', pb: '1rem' }}>
            <strong>{`${data.Name} [${data.UnitMeasurement}]`}</strong>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '0.75rem' }}>
            <Box>
              <strong>Productividad Presupuestada:</strong> {data.Productividad}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <FilterControls
        showAvailableOptions={['week', 'day']}
        defaultViewLevel='week'
        minDate={startDate}
        maxDate={endDate}
        viewLevel={period}
        onViewLevelChange={onPeriodChange}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        showAllOption={currentView === 'table'}
      />
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => setCurrentView('chart')}
                variant={currentView === 'chart' ? 'contained' : 'outlined'}
              >
                Gráfico
              </Button>
              <Button 
                onClick={() => setCurrentView('curve')}
                variant={currentView === 'curve' ? 'contained' : 'outlined'}
              >
                Curva S
              </Button>
              <Button 
                onClick={() => setCurrentView('table')}
                variant={currentView === 'table' ? 'contained' : 'outlined'}
              >
                Tabla
              </Button>
            </ButtonGroup>

            {hideDownloadOptions === 'false' &&<Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              {currentView === 'table' && <Button size="small" variant="outlined" onClick={handleExportExcel}>
                Excel
              </Button>}
              {currentView !== 'table' && <Button size="small" variant="outlined" onClick={handleExportPDF}>
                PDF
              </Button>}
            </Box>}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          {currentView === 'chart' && (
            <div ref={chartRef}>
              <GenericBarChart
                labels={chartData.labels}
                datasets={chartData.datasets}
                title={`Productividad - ${period === 'week' ? 'Semanas' : 'Días'}`}
                yAxisTitle={data.UnitMeasurement || 'Unidades'}
                maxValue={chartData.maxValue}
                shouldUseHorizontalLabels={chartData.shouldUseHorizontalLabels}
                height={450}
              />
            </div>
          )}
          
          {currentView === 'table' && (
            <div ref={tableRef}>
              <GenericTable
                data={tableData}
                columns={productivityTableColumns}
                showHierarchy={false}
                getRowBackgroundColor={getProductivityRowBackgroundColor}
                getRowTextColor={getProductivityRowTextColor}
                getCellBackgroundColor={getProductivityCellBackgroundColor}
                getCellTextColor={getProductivityCellTextColor}
              />
            </div>
          )}
          
          {currentView === 'curve' && (
            <div ref={curveRef}>
              <GenericCurveChart
                labels={curveData.labels}
                datasets={curveData.datasets}
                title={`Curva S - Productividad Acumulada - ${period === 'week' ? 'Semanas' : 'Días'}`}
                yAxisTitle="Productividad Acumulada"
                maxValue={curveData.maxValue}
                shouldUseHorizontalLabels={curveData.shouldUseHorizontalLabels}
                height={450}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};